import { type ReactNode, useState } from 'react';
import { PanelLabel } from '../components/PanelLabel';
import { SectionHeader } from '../components/SectionHeader';
import { useSession } from '../hooks/useSession';
import { revealProps } from '../lib/motion';

// Frontend-only mock of the Wallet section. Balance is wired to the real session
// (the one piece of live plumbing that exists); everything below — packs, deposit
// address/comment, history — is stubbed for the design pass, no backend.

const USD_PER_BID = 0.1;

type Pack = { bids: number; usdt: number; tag: string | null };
const PACKS: Pack[] = [
  { bids: 50, usdt: 5, tag: null },
  { bids: 150, usdt: 14, tag: null },
  { bids: 400, usdt: 36, tag: 'POPULAR' },
  { bids: 1000, usdt: 85, tag: 'BEST VALUE' },
];

type PayPath = 'wallet' | 'newbie';

type TopUpEntry = {
  bids: number;
  usdt: number;
  method: string;
  when: string;
  status: 'confirmed' | 'pending' | 'failed';
};
const RECENT: TopUpEntry[] = [
  { bids: 150, usdt: 14, method: 'Telegram Wallet', when: 'just now', status: 'pending' },
  { bids: 400, usdt: 36, method: 'TON Wallet', when: '2d ago', status: 'confirmed' },
  { bids: 50, usdt: 5, method: 'Tonkeeper', when: '6d ago', status: 'confirmed' },
];

const DEPOSIT_ADDRESS = 'UQCCUAsT51fOSsXW9LQ7FJ2bCcckw5s4Gd4aXG4ChEUXNzd5';
const USDT_JETTON = 'EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs';
const PAYMENT_COMMENT = 'SK-7F3A29';

function tonDeeplink(usdt: number): string {
  return `ton://transfer/${DEPOSIT_ADDRESS}?jetton=${USDT_JETTON}&amount=${usdt * 1_000_000}&text=${PAYMENT_COMMENT}`;
}

function useCopied() {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copy = (key: string, text: string) => {
    navigator.clipboard
      ?.writeText(text)
      .then(() => {
        setCopiedKey(key);
        setTimeout(() => setCopiedKey((c) => (c === key ? null : c)), 1400);
      })
      .catch((error) => console.error('clipboard copy failed', { key, error: String(error) }));
  };
  return { copiedKey, copy };
}

export function Wallet() {
  const [view, setView] = useState<'overview' | 'topup'>('overview');
  const bidBalance = useSession()?.user.bid_balance ?? 0;

  if (view === 'topup') return <TopUpView onBack={() => setView('overview')} />;
  return <Overview balance={bidBalance} onTopUp={() => setView('topup')} />;
}

function Overview({ balance, onTopUp }: { balance: number; onTopUp: () => void }) {
  return (
    <main className="pb-20">
      <BalanceAnchor balance={balance} index={0} />

      <div {...revealProps(1, { className: 'px-3 mt-3' })}>
        <button
          type="button"
          onClick={onTopUp}
          className="w-full h-12 border border-accent bg-accent/12 text-accent font-display font-bold text-[14px] tracking-[0.12em] uppercase flex items-center justify-center gap-2 transition-colors hover:bg-accent/20 active:bg-accent/25"
          style={{
            boxShadow: '0 0 18px rgba(255,61,167,0.18), inset 0 0 22px rgba(255,61,167,0.06)',
          }}
        >
          Buy bids
          <span className="text-[17px] leading-none">+</span>
        </button>
      </div>

      <SectionHeader label="Recent top-ups" count={RECENT.length} />
      <div className="px-3 flex flex-col gap-2">
        {RECENT.map((row, i) => (
          <TopUpRow key={`${row.when}-${row.bids}`} row={row} index={i} />
        ))}
      </div>

      <p
        {...revealProps(RECENT.length + 2, {
          className: 'px-4 mt-4 font-mono text-[9px] leading-relaxed tracking-wider text-fg-dim',
        })}
      >
        Top-ups are credited automatically once the network confirms your payment — usually under a
        minute. Funds are received on-chain in USDT.
      </p>
    </main>
  );
}

function BalanceAnchor({ balance, index }: { balance: number; index: number }) {
  const usd = (balance * USD_PER_BID).toFixed(2);
  const positive = balance > 0;
  return (
    <section
      {...revealProps(index, {
        className:
          'relative mx-3 mt-4 hud-corners overflow-hidden border border-edge bg-bg-elev/40',
      })}
    >
      <div className="absolute inset-0 pointer-events-none scanlines opacity-30" />
      <div
        className="absolute -top-10 left-1/2 -translate-x-1/2 w-56 h-40 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(0,229,255,0.14) 0%, transparent 70%)',
        }}
      />

      <div className="relative px-4 pt-3.5 pb-4">
        <PanelLabel label="Wallet balance" right="USDT · TON" />

        <div className="mt-4 text-center">
          <span className="block font-mono text-[8.5px] tracking-[0.3em] text-fg-muted uppercase">
            Available bids
          </span>
          <span
            className={`block font-mono font-bold text-[46px] leading-none tabular-nums tracking-tight mt-2 ${positive ? 'text-primary' : 'text-fg-dim'}`}
            style={positive ? { textShadow: '0 0 18px rgba(0,229,255,0.45)' } : undefined}
          >
            {balance.toLocaleString('en-US')}
          </span>
          <span className="block font-mono text-[11px] tracking-wider text-fg-muted mt-2.5">
            ≈ ${usd} value
          </span>
        </div>

        <div className="mt-4 pt-3 border-t border-edge grid grid-cols-2">
          <MiniStat label="Topped up" value="1,400" />
          <MiniStat label="Spent on bids" value="160" tone="muted" align="right" />
        </div>
      </div>
    </section>
  );
}

function MiniStat({
  label,
  value,
  tone = 'primary',
  align = 'left',
}: {
  label: string;
  value: string;
  tone?: 'primary' | 'muted';
  align?: 'left' | 'right';
}) {
  return (
    <div className={align === 'right' ? 'text-right' : 'text-left'}>
      <span className="block font-mono text-[8.5px] tracking-[0.18em] text-fg-muted uppercase">
        {label}
      </span>
      <span
        className={`block font-mono font-semibold text-[15px] tabular-nums leading-none mt-1.5 ${tone === 'primary' ? 'text-fg' : 'text-fg-muted'}`}
      >
        {value}
      </span>
    </div>
  );
}

function TopUpRow({ row, index }: { row: TopUpEntry; index: number }) {
  return (
    <div
      {...revealProps(index + 1, {
        className:
          'flex items-center justify-between gap-3 border border-edge bg-bg-elev/30 px-3.5 py-3',
      })}
    >
      <div className="min-w-0">
        <div className="font-mono font-semibold text-[15px] leading-none">
          <span className="text-success">+{row.bids.toLocaleString('en-US')}</span>
          <span className="text-fg-dim text-[10px] ml-1.5">bids</span>
        </div>
        <div className="font-mono text-[9px] tracking-wider text-fg-muted mt-1.5 truncate">
          {row.usdt} USDT · {row.method}
        </div>
      </div>
      <div className="text-right shrink-0">
        <StatusBadge status={row.status} />
        <div className="font-mono text-[9px] tracking-wider text-fg-dim mt-1.5">{row.when}</div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: TopUpEntry['status'] }) {
  if (status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1.5 font-mono text-[9px] tracking-[0.18em] text-warning uppercase">
        <span
          className="w-1.5 h-1.5 rounded-full anim-blink"
          style={{ background: '#ffd93d', boxShadow: '0 0 6px #ffd93d' }}
        />
        Pending
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1.5 font-mono text-[9px] tracking-[0.18em] text-danger uppercase">
        <span className="w-1.5 h-1.5 rounded-full bg-danger" />
        Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-[9px] tracking-[0.18em] text-success uppercase">
      <span
        className="w-1.5 h-1.5 rounded-full bg-success"
        style={{ boxShadow: '0 0 6px #00ff94' }}
      />
      Confirmed
    </span>
  );
}

function TopUpView({ onBack }: { onBack: () => void }) {
  const [packIdx, setPackIdx] = useState(2);
  const [path, setPath] = useState<PayPath>('wallet');
  // PACKS is a non-empty const; the guard only satisfies strict indexed access.
  const pack = PACKS[packIdx] ?? PACKS[0];
  if (!pack) return null;

  return (
    <main className="pb-20">
      <button
        type="button"
        onClick={onBack}
        {...revealProps(0, {
          className:
            'mx-4 mt-4 mb-1 flex items-center gap-1.5 font-mono text-[10px] tracking-[0.22em] text-fg-muted uppercase hover:text-fg transition-colors',
        })}
      >
        <span className="text-[15px] leading-none -mt-0.5">‹</span> Wallet
      </button>

      <h1
        {...revealProps(1, {
          className: 'px-4 font-display font-bold text-[22px] tracking-tight text-fg',
        })}
      >
        Buy bids
      </h1>

      <SectionHeader label="Choose a pack" />
      <div {...revealProps(2, { className: 'px-3 grid grid-cols-2 gap-2' })}>
        {PACKS.map((p, i) => (
          <PackTile key={p.bids} pack={p} selected={i === packIdx} onSelect={() => setPackIdx(i)} />
        ))}
      </div>
      <p className="px-4 mt-2.5 font-mono text-[9px] leading-relaxed tracking-wider text-fg-dim">
        A one-time ~1 USDT network fee applies per top-up. Bigger packs spread it thinner — that's
        why the per-bid price drops.
      </p>

      <SectionHeader label="How you'll pay" />
      <div {...revealProps(3, { className: 'px-3' })}>
        <Segmented value={path} onChange={setPath} />
      </div>

      <div {...revealProps(4, { className: 'px-3 mt-3' })}>
        {path === 'wallet' ? <WalletPath pack={pack} /> : <NewbiePath pack={pack} />}
      </div>
    </main>
  );
}

function PackTile({
  pack,
  selected,
  onSelect,
}: {
  pack: Pack;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`relative text-left px-3.5 py-3 border transition-colors ${
        selected
          ? 'border-primary bg-primary/5 hud-corners'
          : 'border-edge bg-bg-elev/40 hover:border-edge-bright'
      }`}
    >
      {pack.tag && (
        <span className="absolute top-2 right-2 font-mono text-[7.5px] tracking-[0.15em] text-accent border border-accent/40 bg-accent/10 px-1.5 py-0.5 uppercase">
          {pack.tag}
        </span>
      )}
      <span
        className={`block font-mono font-bold text-[24px] leading-none tabular-nums ${selected ? 'text-primary' : 'text-fg'}`}
        style={selected ? { textShadow: '0 0 12px rgba(0,229,255,0.4)' } : undefined}
      >
        {pack.bids.toLocaleString('en-US')}
      </span>
      <span className="block font-mono text-[8.5px] tracking-[0.25em] text-fg-muted uppercase mt-1">
        bids
      </span>
      <span className="block font-mono text-[13px] text-primary mt-3">{pack.usdt} USDT</span>
      <span className="block font-mono text-[8.5px] text-fg-dim mt-0.5">
        ${(pack.usdt / pack.bids).toFixed(3)}/bid
      </span>
    </button>
  );
}

function Segmented({ value, onChange }: { value: PayPath; onChange: (v: PayPath) => void }) {
  const opts: { k: PayPath; label: string }[] = [
    { k: 'wallet', label: 'I have a wallet' },
    { k: 'newbie', label: 'New to crypto' },
  ];
  return (
    <div className="grid grid-cols-2 border border-edge">
      {opts.map((o, i) => {
        const active = value === o.k;
        return (
          <button
            key={o.k}
            type="button"
            onClick={() => onChange(o.k)}
            className={`relative h-9 font-mono text-[10px] tracking-[0.14em] uppercase transition-colors ${
              i === 1 ? 'border-l border-edge' : ''
            } ${active ? 'bg-primary/10 text-primary' : 'text-fg-muted hover:text-fg'}`}
          >
            {o.label}
            {active && <span className="absolute bottom-0 inset-x-0 h-px bg-primary" />}
          </button>
        );
      })}
    </div>
  );
}

function WalletPath({ pack }: { pack: Pack }) {
  return (
    <div className="flex flex-col gap-3">
      <a
        href={tonDeeplink(pack.usdt)}
        className="w-full h-12 border border-accent bg-accent/12 text-accent font-display font-bold text-[14px] tracking-[0.1em] uppercase flex items-center justify-center gap-2 transition-colors hover:bg-accent/20 active:bg-accent/25"
        style={{ boxShadow: '0 0 18px rgba(255,61,167,0.18)' }}
      >
        Pay {pack.usdt} USDT
        <span className="text-[15px] leading-none">→</span>
      </a>
      <p className="font-mono text-[9px] leading-relaxed tracking-wider text-fg-muted text-center px-2">
        Opens Tonkeeper or TON Wallet with the address, amount and comment already filled in. Just
        confirm.
      </p>

      <details className="border border-edge bg-bg-elev/30">
        <summary className="px-3.5 py-2.5 font-mono text-[9.5px] tracking-[0.18em] text-fg-muted uppercase cursor-pointer hover:text-fg list-none flex items-center justify-between">
          Pay manually instead
          <span className="text-fg-dim">＋</span>
        </summary>
        <div className="px-3 pb-3 pt-1">
          <DepositDetails pack={pack} />
        </div>
      </details>
    </div>
  );
}

function NewbiePath({ pack }: { pack: Pack }) {
  return (
    <div className="flex flex-col gap-2.5">
      <StepPanel
        n="01"
        title="Get crypto in Telegram Wallet"
        body="Open the built-in Telegram Wallet and buy a little USDT with your card or via P2P. No app to install, no seed phrase."
      >
        <a
          href="https://t.me/wallet"
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-flex items-center gap-2 h-9 px-3.5 border border-primary/60 text-primary font-mono text-[11px] tracking-[0.12em] uppercase hover:bg-primary/10 transition-colors"
        >
          Open Telegram Wallet
          <span className="text-[13px] leading-none">↗</span>
        </a>
      </StepPanel>

      <StepPanel
        n="02"
        title="Send the payment to us"
        body="In Telegram Wallet: Send → External Wallet → scan the QR (or paste the fields below). Send exactly the amount shown."
      >
        <div className="mt-3">
          <DepositDetails pack={pack} />
        </div>
      </StepPanel>
    </div>
  );
}

function StepPanel({
  n,
  title,
  body,
  children,
}: {
  n: string;
  title: string;
  body: string;
  children?: ReactNode;
}) {
  return (
    <div className="border border-edge bg-bg-elev/30 px-3.5 py-3.5">
      <div className="flex items-start gap-3">
        <span className="font-mono font-bold text-[15px] text-primary/70 tabular-nums leading-none mt-0.5 shrink-0">
          {n}
        </span>
        <div className="min-w-0">
          <h3 className="font-display font-semibold text-[14px] tracking-tight text-fg leading-snug">
            {title}
          </h3>
          <p className="text-[12px] leading-relaxed text-fg-muted mt-1">{body}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

function DepositDetails({ pack }: { pack: Pack }) {
  const { copiedKey, copy } = useCopied();
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex justify-center pt-1">
        <StubQR seed={tonDeeplink(pack.usdt)} />
      </div>

      <CopyField
        label="Address"
        value={DEPOSIT_ADDRESS}
        copied={copiedKey === 'addr'}
        onCopy={() => copy('addr', DEPOSIT_ADDRESS)}
      />
      <CopyField
        label="Amount"
        value={`${pack.usdt} USDT`}
        copied={copiedKey === 'amount'}
        onCopy={() => copy('amount', String(pack.usdt))}
      />
      <CopyField
        label="Comment / memo"
        value={PAYMENT_COMMENT}
        copied={copiedKey === 'comment'}
        onCopy={() => copy('comment', PAYMENT_COMMENT)}
        warn
      />
      <div className="flex items-start gap-2 border border-warning/40 bg-warning/5 px-3 py-2.5">
        <span className="text-warning text-[12px] leading-none mt-px shrink-0">⚠</span>
        <p className="font-mono text-[9.5px] leading-relaxed tracking-wide text-warning">
          The comment is required. Without it your top-up can't be matched to your account and won't
          be credited.
        </p>
      </div>
    </div>
  );
}

function CopyField({
  label,
  value,
  copied,
  onCopy,
  warn = false,
}: {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
  warn?: boolean;
}) {
  return (
    <div
      className={`border px-3 py-2.5 ${warn ? 'border-warning/40 bg-warning/[0.03]' : 'border-edge bg-bg-deep/40'}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span
          className={`font-mono text-[8.5px] tracking-[0.25em] uppercase ${warn ? 'text-warning' : 'text-fg-muted'}`}
        >
          {label}
        </span>
        <button
          type="button"
          onClick={onCopy}
          className={`font-mono text-[9px] tracking-[0.15em] uppercase px-1.5 py-0.5 border transition-colors ${
            copied
              ? 'border-success/50 text-success'
              : 'border-edge text-fg-muted hover:text-fg hover:border-edge-bright'
          }`}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <p className="font-mono text-[12px] text-fg mt-1.5 break-all leading-snug">{value}</p>
    </div>
  );
}

// Decorative pseudo-QR: deterministic module field + 3 finder squares so it reads
// as a QR in the mockup. Not a scannable code — backend wires a real one later.
function StubQR({ seed, size = 132 }: { seed: string; size?: number }) {
  const modules = 25;
  const unit = size / modules;

  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  let s = h >>> 0;
  const rnd = () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };

  const inFinder = (r: number, c: number) => {
    const zones: [number, number][] = [
      [0, 0],
      [0, modules - 7],
      [modules - 7, 0],
    ];
    return zones.some(([zr, zc]) => r >= zr - 1 && r <= zr + 7 && c >= zc - 1 && c <= zc + 7);
  };

  const cells: ReactNode[] = [];
  for (let r = 0; r < modules; r++) {
    for (let c = 0; c < modules; c++) {
      if (inFinder(r, c)) continue;
      if (rnd() > 0.52) {
        cells.push(
          <rect
            key={`${r}-${c}`}
            x={c * unit}
            y={r * unit}
            width={unit}
            height={unit}
            fill="#e6edf3"
          />,
        );
      }
    }
  }

  const finder = (zr: number, zc: number) => (
    <g key={`f-${zr}-${zc}`}>
      <rect x={zc * unit} y={zr * unit} width={unit * 7} height={unit * 7} fill="#e6edf3" />
      <rect
        x={(zc + 1) * unit}
        y={(zr + 1) * unit}
        width={unit * 5}
        height={unit * 5}
        fill="#050810"
      />
      <rect
        x={(zc + 2) * unit}
        y={(zr + 2) * unit}
        width={unit * 3}
        height={unit * 3}
        fill="#00e5ff"
      />
    </g>
  );

  return (
    <div
      className="relative p-2.5 border border-primary/30 bg-bg-deep"
      style={{ width: size + 20 }}
    >
      <span className="absolute -top-px -left-px w-2 h-2 border-t border-l border-primary" />
      <span className="absolute -bottom-px -right-px w-2 h-2 border-b border-r border-primary" />
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label="Payment QR code"
      >
        <rect width={size} height={size} fill="#050810" />
        {cells}
        {finder(0, 0)}
        {finder(0, modules - 7)}
        {finder(modules - 7, 0)}
      </svg>
    </div>
  );
}
